import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CommonHeadingNBtns from "../../common-components/CommonHeadingNBtns";
import MultiSelect from "../../components-react/MultiSelect";
import { selectGymData } from "../../../redux/reducers/gymDataSlice";
import { commonPostPutApi } from "../../../redux/reducers/commonApiSlice";
import { fetchGymData } from "../../../data/APIs";
import { Plus,  XIcon } from "../../../images/icons/icons";
import useCloudFrontUpload from "../../../hooks/useCloudFrontUpload";
import "../../../styles/offers/add-gym-offer.css";

const AddGymOffersForm = ({ isEdit, rowData, closeModal, callFunction, userInfoData }) => {
  const dispatch = useDispatch();
  const gymData = useSelector(selectGymData);
  const { uploadToCloudfront, isUploading } = useCloudFrontUpload();
  
  const forminitials = {
    offerName: "",
    selectedOfferCategory: "",
    offerBannerURL: "",
    offerDescription: "",
    selectedGyms: [],
    offerBenifits: [], 
    offerStartDate: "",
    offerEndDate: "",
    actualPrice: "",
    offerPrice: "",
    offerLandingPageURL: "",
    isActive: isEdit ? (rowData?.status === "active" ? true : false) : false,
  };
  const [formValues, setFormValues] = useState(forminitials);
  const [formErrors, setFormErrors] = useState({});
  const [currentBenefit, setCurrentBenefit] = useState(""); 
  const [formImages, setFormImages] = useState({
    bannerMedia: rowData?.banner_url ? [{ data_url: rowData.banner_url }] : []
  });
  const [uploadedFile, setUploadedFile] = useState(null);

  // Gym options for multiselect
  const gymOptions = gymData?.map(gym => ({
    value: gym.info?.uid ,
    label: gym.info?.label || gym.label || `${gym.gymName || gym.name} - ${gym.location || ''}`
  })) || [];
  
  // Utility function to normalize text for API
  const normalizeText = (text) => {
    if (!text || typeof text !== 'string') return '';
    return text
      .trim() 
      .toLowerCase() 
      .replace(/\s+/g, '_'); 
  };

  // Utility to de-normalize for display (e.g., october_sale -> October Sale)
  const humanize = (text) => {
    if (!text || typeof text !== 'string') return '';
    return text
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Form handling functions
  const onChangeInput = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const onChangeDropdown = (selectedValue, fieldName) => {
    setFormValues(prev => ({
      ...prev,
      [fieldName]: selectedValue
    }));
    
    // Clear error when user selects
    if (formErrors[fieldName]) {
      setFormErrors(prev => ({
        ...prev,
        [fieldName]: ""
      }));
    }
  };

  const addBenefit = () => {
    if (!currentBenefit.trim()) {
      toast.error("Please enter a benefit before adding");
      return;
    }
    
    // Check for duplicate benefits
    if (formValues.offerBenifits.includes(currentBenefit.trim())) {
      toast.error("This benefit already exists");
      return;
    }
    
    // Add benefit to the list
    setFormValues(prev => ({
      ...prev,
      offerBenifits: [...prev.offerBenifits, currentBenefit.trim()]
    }));
    
    // Clear input and error
    setCurrentBenefit("");
    if (formErrors.offerBenifits) {
      setFormErrors(prev => ({
        ...prev,
        offerBenifits: ""
      }));
    }
  };

  const removeBenefit = (index) => {
    setFormValues(prev => ({
      ...prev,
      offerBenifits: prev.offerBenifits.filter((_, i) => i !== index)
    }));
  };

  const handleBenefitInputChange = (e) => {
    setCurrentBenefit(e.target.value);
    // Clear error when user starts typing
    if (formErrors.offerBenifits) {
      setFormErrors(prev => ({
        ...prev,
        offerBenifits: ""
      }));
    }
  };
  // Custom file input handler for both images and videos
  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Validate file type
    const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    const validVideoTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];
    const isValidFile = [...validImageTypes, ...validVideoTypes].includes(file.type);

    if (!isValidFile) {
      toast.error("Please select a valid image (jpg, png, webp) or video (mp4, mov, avi) file");
      return;
    }
    // Create preview URL
    const previewUrl = URL.createObjectURL(file);
    // Update preview
    setFormImages({
      bannerMedia: [{ data_url: previewUrl, file }]
    });

    setUploadedFile(file);

    await handleUploadImage(file, "bannerMedia");
  };

  // // Image/Video upload functions (keeping for backward compatibility with ImageUploading)
  // const onChangeImages = (imageList, name) => {
  //   console.log("🔍 onChangeImages called with:", { imageList, name });

  //   setFormImages((prevImages) => {
  //     const uniqueImages = [
  //       ...new Set([...(prevImages[name] || []), ...imageList]),
  //     ].slice(0, 1);

  //     return {
  //       ...prevImages,
  //       [name]: uniqueImages,
  //     };
  //   });

  //   const previousImages = formImages[name] || [];
  //   const newImages = imageList.filter(
  //     (newImage) =>
  //       !previousImages.some(
  //         (prevImage) => prevImage.data_url === newImage.data_url
  //       )
  //   );

  //   console.log("🔍 New images to upload:", newImages);

  //   if (newImages.length > 0) {
  //     newImages.forEach(async (image) => {
  //       console.log("🔍 Processing image:", image);
  //       console.log("🔍 Image data_url type:", typeof image?.data_url);
  //       console.log("🔍 Image data_url preview:", image?.data_url?.substring(0, 50));

  //       const blob = await fetch(image?.data_url).then((res) => res.blob());
  //       console.log("🔍 Blob created:", { type: blob.type, size: blob.size });

  //       // Detect if it's a video or image based on blob type
  //       const isVideo = blob.type.startsWith('video/');
  //       const fileExtension = isVideo
  //         ? blob.type.split('/')[1] || 'mp4'
  //         : blob.type.split('/')[1] || 'jpeg';
  //       const fileName = `banner.${fileExtension}`;

  //       console.log("🔍 File info:", { isVideo, fileExtension, fileName, blobType: blob.type });

  //       const file = new File([blob], fileName, { type: blob.type });
  //       console.log("🔍 File created:", { name: file.name, type: file.type, size: file.size });

  //       handleUploadImage(file, name);
  //     });
  //   }
  // };

  const handleImageRemove = (index) => {
    setFormImages((prevImages) => {
      const updatedImages = [...prevImages.bannerMedia];
      updatedImages.splice(index, 1);
      return {
        ...prevImages,
        bannerMedia: updatedImages,
      };
    });

    setFormValues((prevValues) => ({
      ...prevValues,
      offerBannerURL: "",
    }));
  };

  const handleUploadImage = async (file, name) => {
    const TOKEN = userInfoData?.token;

    try {
      const imageUrl = await uploadToCloudfront(file, TOKEN);
      if (imageUrl) {
        setFormValues((prev) => ({
          ...prev,
          offerBannerURL: imageUrl,
        }));
        toast.success("Media uploaded successfully!");
      } else {
        toast.error("Upload failed!");
      }
    } catch (error) {
      toast.error("Upload failed!");
    }
  };

  const validateForm = () => {
    const errors = {};
    console.log("errors => ", errors);
    
    if (!formValues.offerName.trim()) {
      errors.offerName = "Offer name is required";
    }
    
    if (!formValues.selectedOfferCategory) {
      errors.selectedOfferCategory = "Offer category is required";
    }
    
    if (!formValues.offerDescription.trim()) {
      errors.offerDescription = "Offer description is required";
    }
    
    if (formValues.selectedGyms.length === 0) {
      errors.selectedGyms = "At least one gym must be selected";
    }
    
    const validBenefits = formValues.offerBenifits.filter(benefit => benefit.trim());
    if (validBenefits.length === 0) {
      errors.offerBenifits = "At least one benefit is required";
    }
    
    if (!formValues.offerStartDate) {
      errors.offerStartDate = "Start date is required";
    }
    
    if (!formValues.offerEndDate) {
      errors.offerEndDate = "End date is required";
    }
    
    if (!formValues.actualPrice) {
      errors.actualPrice = "Actual price is required";
    }
    
    if (!formValues.offerPrice) {
      errors.offerPrice = "Offer price is required";
    }
    // Convert to numbers for proper comparison
    const actualPriceNum = parseFloat(formValues.actualPrice);
    const offerPriceNum = parseFloat(formValues.offerPrice);
    
    if (offerPriceNum > actualPriceNum) {
      errors.offerPrice = "Offer price cannot be greater than actual price";
    }
    
    // Date validation
    if (formValues.offerStartDate && formValues.offerEndDate) {
      if (new Date(formValues.offerStartDate) >= new Date(formValues.offerEndDate)) {
        errors.offerEndDate = "End date must be after start date";
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitForm = async (method, endpoint, payload) => {
    const token = userInfoData?.token || "";

    try {
      const result = await dispatch(
        commonPostPutApi({ method, endpoint, payload, token })
      );

      if (result?.payload?.status) {
        toast.success(result?.payload?.message || "Offer created successfully!");
        closeModal();
        if (callFunction) callFunction();
      } else {
        const errorMessage = result?.payload?.message || result?.error?.message;
        toast.error(errorMessage || "Something Went Wrong!");
      }
    } catch (error) {
      console.error("Error creating offer:", error);
      toast.error("Failed to create offer");
    }
  };

  const onSubmit = () => {
    if (validateForm()) {
      const payload = {
        title: normalizeText(formValues.offerName), 
        description: formValues.offerDescription,
        image_url: formValues.offerBannerURL,
        landing_page_url: formValues.offerLandingPageURL,
        campaign_name: normalizeText(formValues.selectedOfferCategory), 
        status: formValues.isActive ? "active" : "inactive",
        valid_from: formValues.offerStartDate,
        valid_to: formValues.offerEndDate,
        gym_ids: formValues.selectedGyms.map(gym => gym.value),
        actual_price: formValues.actualPrice,
        offer_price: formValues.offerPrice,
        benefits: formValues.offerBenifits.filter(benefit => benefit.trim())
      };
      const method = isEdit ? "PUT" : "POST";
      const endpoint = isEdit && rowData?.uid ? `/ongoing_offer/${rowData.uid}` : "/ongoing_offer/add";
      console.log("Payload being sent:", { method, endpoint, payload });
      handleSubmitForm(method, endpoint, payload);
    } else {
      toast.error("Please fix the errors before submitting");
    }
  };

  // Fetch gym data when component mounts
  useEffect(() => {
    if (userInfoData?.token) {
      dispatch(fetchGymData(userInfoData.token));
    }
  }, [dispatch, userInfoData?.token]);

  // Prefill form in edit mode from rowData (except gyms which depend on options)
  useEffect(() => {
    if (!isEdit || !rowData) return;

    setFormValues(prev => ({
      ...prev,
      offerName: humanize(rowData.title || ""),
      selectedOfferCategory: humanize(rowData.campaign_name || ""),
      offerBannerURL: rowData.image_url || "",
      offerDescription: rowData.description || "",
      offerBenifits: Array.isArray(rowData.benefits) ? rowData.benefits : [],
      offerStartDate: rowData.valid_from || "",
      offerEndDate: rowData.valid_to || "",
      actualPrice: rowData.actual_price || "",
      offerPrice: rowData.offer_price || "",
      offerLandingPageURL: rowData.landing_page_url || "",
      isActive: rowData.status === "active"
    }));

    // Prefill banner preview if present
    if (rowData.image_url) {
      setFormImages({ bannerMedia: [{ data_url: rowData.image_url }] });
    }
  }, [isEdit, rowData]);

  // Map gyms from rowData once options are available
  useEffect(() => {
    if (!isEdit || !rowData || !Array.isArray(rowData.gyms)) return;
    if (!Array.isArray(gymOptions) || gymOptions.length === 0) return;

    if (formValues.selectedGyms && formValues.selectedGyms.length > 0) return;

    const mapped = rowData.gyms
      .map(g => {
        const opt = gymOptions.find(o => o.value === (g.gym_id || g.value));
        if (opt) return opt;
        return { value: g.gym_id, label: g.gym_name };
      })
      .filter(Boolean);

    setFormValues(prev => ({
      ...prev,
      selectedGyms: mapped
    }));
  }, [isEdit, rowData, gymOptions]);

  return (
    <div className="createNewCTOModalContainer mealsModalContainer mod-width-70p">
      <CommonHeadingNBtns
        title={`${isEdit ? "Update" : "Add"} Offer`}
        onClose={closeModal}
        btnTxt={isEdit ? "Update" : "Submit"}
        onClick={onSubmit}
        isLoading={isUploading}
      />

      <form className="formContainer">
        {/* Basic Information */}
        <div className="formInputsGrid fourGridSystem">
          <div className="formInputBox">
            <label htmlFor="offerName" className="formInputLabel">
              Offer Name <span className="required">*</span>
            </label>
            <input
              type="text"
              className={`formInput ${formErrors?.offerName ? "formError" : ""}`}
              name="offerName"
              placeholder="Enter offer name"
              value={formValues.offerName}
              onChange={onChangeInput}
            />
            {formErrors?.offerName && (
              <span className="error-label">{formErrors.offerName}</span>
            )}
          </div>

          <div className="formInputBox">
            <label htmlFor="selectedOfferCategory" className="formInputLabel">
              Offer Category <span className="required">*</span>
            </label>
            <div className={`commonWrapper ${formErrors?.selectedOfferCategory ? "formError" : ""}`}>
            <input
              type="text"
              className={`formInput ${formErrors?.selectedOfferCategory ? "formError" : ""}`}
              name="selectedOfferCategory"
              placeholder="Enter offer Category"
              value={formValues.selectedOfferCategory}
              onChange={onChangeInput}
            />
            </div>
            {formErrors?.selectedOfferCategory && (
              <span className="error-label">{formErrors.selectedOfferCategory}</span>
            )}
          </div>
          <div className="formInputBox">
            <label htmlFor="offerStartDate" className="formInputLabel">
              Start Date <span className="required">*</span>
            </label>
            <input
              type="date"
              className={`formInput ${formErrors?.offerStartDate ? "formError" : ""}`}
              name="offerStartDate"
              value={formValues.offerStartDate}
              onChange={onChangeInput}
            />
            {formErrors?.offerStartDate && (
              <span className="error-label">{formErrors.offerStartDate}</span>
            )}
          </div>

          <div className="formInputBox">
            <label htmlFor="offerEndDate" className="formInputLabel">
              End Date <span className="required">*</span>
            </label>
            <input
              type="date"
              className={`formInput ${formErrors?.offerEndDate ? "formError" : ""}`}
              name="offerEndDate"
              value={formValues.offerEndDate}
              onChange={onChangeInput}
            />
            {formErrors?.offerEndDate && (
              <span className="error-label">{formErrors.offerEndDate}</span>
            )}
          </div>
        </div>

        {/* Date Range and Pricing */}
        <div className="formInputsGrid fourGridSystem">
          <div className="formInputBox">
            <label htmlFor="actualPrice" className="formInputLabel">
              Actual Price <span className="required">*</span>
            </label>
            <input
              type="number"
              className={`formInput ${formErrors?.actualPrice ? "formError" : ""}`}
              name="actualPrice"
              placeholder="Enter actual price"
              value={formValues.actualPrice}
              onChange={onChangeInput}
              min="0"
            />
            {formErrors?.actualPrice && (
              <span className="error-label">{formErrors.actualPrice}</span>
            )}
          </div>
          <div className="formInputBox">
            <label htmlFor="offerPrice" className="formInputLabel">
              Offer Price <span className="required">*</span>
            </label>
            <input
              type="number"
              className={`formInput ${formErrors?.offerPrice ? "formError" : ""}`}
              name="offerPrice"
              placeholder="Enter offer price"
              value={formValues.offerPrice}
              onChange={onChangeInput}
              min="0"
            />
            {formErrors?.offerPrice && (
              <span className="error-label">{formErrors.offerPrice}</span>
            )}
          </div>
          <div className="formInputBox">
            <label htmlFor="offerLandingPageURL" className="formInputLabel">
              Landing Page URL
            </label>
            <input
              type="url"
              className="formInput"
              name="offerLandingPageURL"
              placeholder="Enter landing page URL"
              value={formValues.offerLandingPageURL}
              onChange={onChangeInput}
            />
          </div>
        </div>

        {/* Gyms selection */}
        <div className="formInputsGrid oneGridSystem mb-5">
          <div className="formInputBox">
            <label htmlFor="selectedGyms" className="formInputLabel">
              Select Gyms <span className="required">*</span>
            </label>
            <div className={`commonWrapper no-border multi-select multi-show-option ${formErrors?.selectedGyms ? "formError" : ""}`}>
              <MultiSelect
                options={gymOptions}
                state={formValues.selectedGyms}
                setState={(selectedValue) => onChangeDropdown(selectedValue, "selectedGyms")}
                name="Select Gyms"
                preventKeyboardOpen={false}
                isShowList={true}
              />
            </div>
            {formErrors?.selectedGyms && (
              <span className="error-label">{formErrors.selectedGyms}</span>
            )}
          </div>
        </div>

          {/* Description */}
          <div className="formInputsGrid oneGridSystem">
          <div className="formInputBox">
            <label htmlFor="offerDescription" className="formInputLabel">
              Offer Description <span className="required">*</span>
            </label>
            <textarea
              className={`formInput ${formErrors?.offerDescription ? "formError" : ""}`}
              name="offerDescription"
              placeholder="Enter detailed offer description"
              value={formValues.offerDescription}
              onChange={onChangeInput}
              rows={4}
            />
            {formErrors?.offerDescription && (
              <span className="error-label">{formErrors.offerDescription}</span>
            )}
          </div>
        </div>
        
        {/* Banner Media Upload */}
        <div className="formInputsGrid twoGridSystem ">
          <div className="formInputBox">
            <label htmlFor="bannerMedia" className="formInputLabel">
              Banner Media (Image/Video)
            </label>
            <div className="commonWrapper commonUploadWrapper no-border" style={{ height: "auto" }}>
              <div className="icon-upload-btn createIncentiveCategoryForImageUpload">
                <div className="icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="49"
                    height="49"
                    viewBox="0 0 49 49"
                    fill="none"
                  >
                    <path
                      d="M35.2188 24.9431C34.3574 24.0828 33.1898 23.5996 31.9725 23.5996C30.7552 23.5996 29.5876 24.0828 28.7262 24.9431L10.7188 42.874C13.3066 42.874 21.1006 42.874 38.2812 42.874C39.8253 42.8683 41.3316 42.3958 42.6023 41.5186C43.873 40.6414 44.8488 39.4004 45.4016 37.9587C45.4016 37.7749 45.5394 37.5759 45.6006 37.3768L45.7844 36.749C45.8349 36.5064 45.8707 36.2609 45.8916 36.014C45.8985 35.9478 45.8985 35.8811 45.8916 35.8149C45.8978 35.7846 45.8978 35.7534 45.8916 35.7231L35.2188 24.9431Z"
                      fill="#A5A5A5"
                    />
                    <path
                      d="M45.9355 13.0463C45.7522 11.1457 44.8653 9.38218 43.4489 8.10171C42.0324 6.82125 40.1886 6.11626 38.2792 6.12508H10.7167C8.8073 6.11626 6.96351 6.82125 5.54706 8.10171C4.13061 9.38218 3.24375 11.1457 3.06046 13.0463C3.04235 13.291 3.04235 13.5367 3.06046 13.7813V35.2188C3.04567 35.484 3.04567 35.7499 3.06046 36.0151C3.09821 36.2728 3.14932 36.5283 3.21359 36.7807C3.7609 38.4437 4.70723 39.9473 5.96984 41.1601C6.36281 41.4953 6.80095 41.7737 7.2714 41.987L26.5192 22.7545C27.2304 22.0415 28.0753 21.4758 29.0054 21.0898C29.9356 20.7038 30.9328 20.5051 31.9398 20.5051C32.9469 20.5051 33.9441 20.7038 34.8742 21.0898C35.8044 21.4758 36.6493 22.0415 37.3605 22.7545L45.9355 31.3295V13.7813C45.9536 13.5367 45.9536 13.291 45.9355 13.0463ZM12.248 18.3751C11.6423 18.3751 11.0502 18.1955 10.5465 17.859C10.0429 17.5224 9.65037 17.0441 9.41858 16.4845C9.18679 15.925 9.12614 15.3092 9.24431 14.7151C9.36247 14.1211 9.65415 13.5754 10.0824 13.1471C10.5107 12.7188 11.0564 12.4271 11.6505 12.3089C12.2446 12.1908 12.8603 12.2514 13.4199 12.4832C13.9795 12.715 14.4578 13.1075 14.7943 13.6111C15.1308 14.1148 15.3105 14.7069 15.3105 15.3126C15.3105 16.1248 14.9878 16.9038 14.4135 17.4781C13.8391 18.0524 13.0602 18.3751 12.248 18.3751Z"
                      fill="#A5A5A5"
                    />
                  </svg>
                </div>
                <div className="image">
                  <div className="upload__image-wrapper">
                    {formImages.bannerMedia?.length === 0 ? (
                      <>
                        <input
                          type="file"
                          id="bannerMediaInput"
                          accept="image/jpeg,image/jpg,image/png,image/webp,video/mp4,video/quicktime,video/x-msvideo"
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                        />
                        <button
                          type="button"
                          onClick={() => document.getElementById('bannerMediaInput').click()}
                        >
                          Upload
                        </button>
                        &nbsp;
                      </>
                    ) : (
                      <>
                        {formImages.bannerMedia.map((media, index) => (
                          <div key={index} className="image-item">
                            {media.file?.type?.startsWith('video') || media.data_url?.includes('video') || media.data_url?.includes('.mp4') || media.data_url?.includes('.mov') || media.data_url?.includes('.avi') ? (
                              <video src={media.data_url} width="200" height="100" controls />
                            ) : (
                              <img src={media.data_url} alt="" width="200" height="100" />
                            )}
                            <div className="image-item__btn-wrapper">
                              <button
                                type="button"
                                onClick={() => document.getElementById('bannerMediaInput').click()}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  className="bi bi-cloud-plus"
                                  viewBox="0 0 16 16"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M8 5.5a.5.5 0 0 1 .5.5v1.5H10a.5.5 0 0 1 0 1H8.5V10a.5.5 0 0 1-1 0V8.5H6a.5.5 0 0 1 0-1h1.5V6a.5.5 0 0 1 .5-.5z"
                                  />
                                  <path d="M4.406 3.342A5.53 5.53 0 0 1 8 2c2.69 0 4.923 2 5.166 4.579C14.758 6.804 16 8.137 16 9.773 16 11.569 14.502 13 12.687 13H3.781C1.708 13 0 11.366 0 9.318c0-1.763 1.266-3.223 2.942-3.593.143-.863.698-1.723 1.464-2.383zm.653.757c-.757.653-1.153 1.44-1.153 2.056v.448l-.445.049C2.064 6.805 1 7.952 1 9.318 1 10.785 2.23 12 3.781 12h8.906C13.98 12 15 10.988 15 9.773c0-1.216-1.02-2.228-2.313-2.228h-.5v-.5C12.188 4.825 10.328 3 8 3a4.53 4.53 0 0 0-2.941 1.1z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => {
                                  handleImageRemove(index);
                                  setUploadedFile(null);
                                }}
                              >
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  width="16"
                                  height="16"
                                  fill="currentColor"
                                  className="bi bi-x"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))}
                        <input
                          type="file"
                          id="bannerMediaInput"
                          accept="image/jpeg,image/jpg,image/png,image/webp,video/mp4,video/quicktime,video/x-msvideo"
                          onChange={handleFileSelect}
                          style={{ display: 'none' }}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            {isUploading && (
              <div className="upload-loading">
                <span>Uploading...</span>
              </div>
            )}
          </div>
          <div className="formInputBox">
            <label className="formInputLabel">
              Offer Benefits <span className="required">*</span>
            </label>
            <div className="benefits-container">
              {/* Input box for entering benefits */}
              <input
                type="text"
                className={`formInput ${formErrors?.offerBenifits ? "formError" : ""}`}
                placeholder="Enter a benefit and click Add Benefit"
                value={currentBenefit}
                onChange={handleBenefitInputChange}
              />
              
              {/* Benefit chips/pills */}
              {formValues.offerBenifits.length > 0 && (
                <div className="benefit-chips-container">
                  {formValues.offerBenifits.map((benefit, index) => (
                    <div key={index} className="benefit-chip">
                      <span className="benefit-chip-text">{benefit}</span>
                      <button
                        type="button"
                        className="benefit-chip-remove"
                        onClick={() => removeBenefit(index)}
                        title="Remove benefit"
                      >
                        <XIcon color="#ef4444" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add benefit button */}
              <button
                type="button"
                className="add-benefit-btn"
                onClick={addBenefit}
                title="Add benefit"
              >
                <Plus color="#22c55e" />
                Add Benefit
              </button>
            </div>
            {formErrors?.offerBenifits && (
              <span className="error-label">{formErrors.offerBenifits}</span>
            )}
          </div>
        </div>

        {/* Checkboxes */}
        <div className="formInputBox">
          <div className="flex-box">
            <label className="checkboxLabel">
              <input
                type="checkbox"
                name="isActive"
                checked={formValues.isActive}
                onChange={onChangeInput}
              />
              Active
            </label>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddGymOffersForm;
